import { useState, useEffect } from 'react';
import { HelpCircle, Building, Search, Plus, Edit, Trash2, Clock, MessageSquare, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import apiClient from '../api/client';
import { CompanyDto, FaqItem, SupportTicketResponseDto } from '../types/dtos';
import FeedbackDialog, { FeedbackType } from '../components/FeedbackDialog';

export default function HelpCenter() {
    const [activeTab, setActiveTab] = useState<'faqs' | 'tickets'>('faqs');
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [tickets, setTickets] = useState<SupportTicketResponseDto[]>([]);
    const [companies, setCompanies] = useState<CompanyDto[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    // FAQ Modal state
    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
    const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
    const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '', displayOrder: 0, isActive: true });

    // Ticket View/Status state
    const [selectedTicket, setSelectedTicket] = useState<SupportTicketResponseDto | null>(null);

    // Feedback Dialog state
    const [feedback, setFeedback] = useState<{
        isOpen: boolean;
        type: FeedbackType;
        title: string;
        message: string;
        primaryActionLabel?: string;
        onPrimaryAction?: () => void;
        secondaryActionLabel?: string;
    }>({
        isOpen: false,
        type: 'success',
        title: '',
        message: '',
    });

    const triggerFeedback = (config: Omit<typeof feedback, 'isOpen'>) => {
        setFeedback({ ...config, isOpen: true });
    };

    const isSuperAdmin = user?.company === 'Xtrem Group';

    // --- Fetch Base Data ---
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const profileRes = await apiClient.get('/admin/profile');
                setUser(profileRes.data);

                if (profileRes.data.company === 'Xtrem Group') {
                    const companiesRes = await apiClient.get<CompanyDto[]>('/admin/companies');
                    setCompanies(companiesRes.data);
                }
            } catch (err: any) {
                console.error("Failed to load initial data", err);
            }
        };
        fetchInitialData();
    }, []);

    // --- Fetch Data (depends on activeTab and selectedCompany) ---
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const companyParam = selectedCompany ? `?companyId=${selectedCompany}` : '';
                if (activeTab === 'faqs') {
                    const res = await apiClient.get<FaqItem[]>(`/admin/faqs${companyParam}`);
                    setFaqs(res.data);
                } else {
                    const res = await apiClient.get<SupportTicketResponseDto[]>(`/admin/support/tickets${companyParam}`);
                    setTickets(res.data);
                }
            } catch (error: any) {
                console.error(`Failed to load ${activeTab}`, error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [activeTab, selectedCompany]);

    // --- Actions ---
    const handleCreateOrUpdateFaq = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingFaq) {
                await apiClient.put(`/admin/faqs/${editingFaq.id}`, faqForm);
            } else {
                await apiClient.post('/admin/faqs', faqForm);
            }
            setIsFaqModalOpen(false);
            setEditingFaq(null);
            // Refresh Faqs
            const companyParam = selectedCompany ? `?companyId=${selectedCompany}` : '';
            const faqsRes = await apiClient.get<FaqItem[]>(`/admin/faqs${companyParam}`);
            setFaqs(faqsRes.data);
            triggerFeedback({
                type: 'success',
                title: 'FAQ Saved',
                message: `FAQ has been ${editingFaq ? 'updated' : 'created'} successfully.`,
            });
        } catch (error: any) {
            console.error('Failed to save FAQ:', error);
            triggerFeedback({
                type: 'error',
                title: 'Save Failed',
                message: error.response?.data?.message || 'Failed to save FAQ. Please try again.',
            });
        }
    };

    const handleDeleteFaq = async (id: string) => {
        triggerFeedback({
            type: 'confirm',
            title: 'Delete FAQ',
            message: 'Are you sure you want to delete this FAQ? This action cannot be undone.',
            primaryActionLabel: 'Yes, Delete',
            onPrimaryAction: async () => {
                try {
                    await apiClient.delete(`/admin/faqs/${id}`);
                    setFaqs(prev => prev.filter(f => f.id !== id));
                    triggerFeedback({
                        type: 'success',
                        title: 'FAQ Deleted',
                        message: 'FAQ has been deleted successfully.',
                    });
                } catch (error: any) {
                    console.error('Failed to delete FAQ:', error);
                    triggerFeedback({
                        type: 'error',
                        title: 'Delete Failed',
                        message: error.response?.data?.message || 'Failed to delete FAQ. Please try again.',
                    });
                }
            },
            secondaryActionLabel: 'Cancel',
        });
    };

    const handleUpdateTicketStatus = async (ticketId: string, status: string) => {
        try {
            await apiClient.put(`/admin/support/tickets/${ticketId}/status`, { status });
            setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: status as any } : t));
            if (selectedTicket?.id === ticketId) {
                setSelectedTicket(prev => prev ? { ...prev, status: status as any } : null);
            }
            triggerFeedback({
                type: 'success',
                title: 'Status Updated',
                message: `Ticket status has been updated to ${status}.`,
            });
        } catch (error: any) {
            console.error('Failed to update ticket status:', error);
            triggerFeedback({
                type: 'error',
                title: 'Update Failed',
                message: error.response?.data?.message || 'Failed to update ticket status. Please try again.',
            });
        }
    };

    const openFaqModal = (faq?: FaqItem) => {
        if (faq) {
            setEditingFaq(faq);
            setFaqForm({
                question: faq.question,
                answer: faq.answer,
                category: faq.category || '',
                displayOrder: faq.displayOrder,
                isActive: faq.isActive
            });
        } else {
            setEditingFaq(null);
            setFaqForm({ question: '', answer: '', category: '', displayOrder: 0, isActive: true });
        }
        setIsFaqModalOpen(true);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString() + ' ' +
            new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="w-full">
            <div className="max-w-7xl mx-auto">

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <HelpCircle className="text-blue-900" /> Help Center
                        </h1>

                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex space-x-1 bg-white p-1 rounded-lg border border-gray-200 shadow-sm mr-4">
                                <button
                                    onClick={() => setActiveTab('faqs')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'faqs'
                                        ? 'bg-blue-900 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    <HelpCircle size={14} /> FAQs
                                </button>
                                <button
                                    onClick={() => setActiveTab('tickets')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'tickets'
                                        ? 'bg-blue-900 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    <MessageSquare size={14} /> Contact Us
                                </button>
                            </div>

                            {isSuperAdmin && (
                                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-1.5 shadow-sm">
                                    <Building size={16} className="text-gray-400" />
                                    <select
                                        value={selectedCompany}
                                        onChange={(e) => setSelectedCompany(e.target.value)}
                                        className="text-sm font-medium focus:outline-none bg-transparent"
                                    >
                                        <option value="">All Companies</option>
                                        {companies.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {activeTab === 'faqs' && (
                                <button
                                    onClick={() => openFaqModal()}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 shadow-sm transition-all"
                                >
                                    <Plus size={16} /> New FAQ
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {loading ? (
                            <div className="p-12 text-center text-gray-400 flex flex-col items-center gap-3">
                                <Clock size={32} className="animate-pulse" />
                                Loading {activeTab === 'faqs' ? 'FAQs' : 'Tickets'}...
                            </div>
                        ) : activeTab === 'faqs' ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                                        <tr>
                                            <th className="px-6 py-4 w-1/3">Question</th>
                                            <th className="px-6 py-4 w-1/3">Answer</th>
                                            <th className="px-6 py-4">Category</th>
                                            <th className="px-6 py-4 text-center">Order</th>
                                            <th className="px-6 py-4">Status</th>
                                            <th className="px-6 py-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {faqs.length === 0 ? (
                                            <tr><td colSpan={6} className="p-12 text-center text-gray-500">No FAQs found. Click "New FAQ" to add one.</td></tr>
                                        ) : (
                                            faqs.map((faq) => (
                                                <tr key={faq.id} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 font-medium text-gray-900">{faq.question}</td>
                                                    <td className="px-6 py-4 text-gray-600 max-w-md truncate">{faq.answer}</td>
                                                    <td className="px-6 py-4 text-gray-500">{faq.category || 'General'}</td>
                                                    <td className="px-6 py-4 text-gray-500 text-center">{faq.displayOrder}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${faq.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                            {faq.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex justify-end gap-2">
                                                            <button onClick={() => openFaqModal(faq)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit size={16} /></button>
                                                            <button onClick={() => handleDeleteFaq(faq.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            // --- TICKETS TABLE ---
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                                        <tr>
                                            <th className="px-6 py-4">Ticket ID</th>
                                            <th className="px-6 py-4">Customer</th>
                                            <th className="px-6 py-4">Subject</th>
                                            <th className="px-6 py-4">Status</th>
                                            <th className="px-6 py-4">Created At</th>
                                            <th className="px-6 py-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {tickets.length === 0 ? (
                                            <tr><td colSpan={6} className="p-12 text-center text-gray-500">No support tickets found.</td></tr>
                                        ) : (
                                            tickets.map((ticket) => (
                                                <tr key={ticket.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => setSelectedTicket(ticket)}>
                                                    <td className="px-6 py-4 font-mono font-bold text-blue-700">{ticket.ticketId}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-medium text-gray-900">{ticket.customerName}</div>
                                                        <div className="text-gray-500 text-xs">{ticket.customerEmail}</div>
                                                    </td>
                                                    <td className="px-6 py-4 text-gray-900 font-medium">{ticket.subject}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ticket.status === 'Open' ? 'bg-blue-100 text-blue-800' :
                                                            ticket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                                                                ticket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {ticket.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-gray-500">{formatDate(ticket.createdAt)}</td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button className="text-blue-600 hover:underline">Details</button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* FAQ Modal */}
                    {isFaqModalOpen && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                                <div className="p-6 border-b border-gray-100">
                                    <h2 className="text-xl font-bold text-gray-900">{editingFaq ? 'Edit FAQ' : 'New FAQ'}</h2>
                                </div>
                                <form onSubmit={handleCreateOrUpdateFaq} className="p-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                                        <textarea
                                            value={faqForm.question}
                                            onChange={e => setFaqForm({ ...faqForm, question: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            rows={2} required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                                        <textarea
                                            value={faqForm.answer}
                                            onChange={e => setFaqForm({ ...faqForm, answer: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            rows={4} required
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                            <input
                                                type="text" value={faqForm.category}
                                                onChange={e => setFaqForm({ ...faqForm, category: e.target.value })}
                                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                placeholder="e.g. Claims, Account"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                                            <input
                                                type="number" value={faqForm.displayOrder}
                                                onChange={e => setFaqForm({ ...faqForm, displayOrder: parseInt(e.target.value) })}
                                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox" checked={faqForm.isActive}
                                            onChange={e => setFaqForm({ ...faqForm, isActive: e.target.checked })}
                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <label className="text-sm font-medium text-gray-700">Make this FAQ active and visible</label>
                                    </div>
                                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                        <button type="button" onClick={() => setIsFaqModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">Cancel</button>
                                        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Save FAQ</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Ticket Details Modal */}
                    {selectedTicket && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
                                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Ticket Details</h2>
                                        <p className="text-sm font-mono text-blue-600 font-bold">{selectedTicket.ticketId}</p>
                                    </div>
                                    <button onClick={() => setSelectedTicket(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                                </div>
                                <div className="p-6 space-y-6">
                                    <div className="flex flex-col md:flex-row gap-6">
                                        <div className="flex-1 space-y-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Customer</label>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                                                        {selectedTicket.customerName.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-gray-900">{selectedTicket.customerName}</div>
                                                        <div className="text-gray-500 text-sm flex items-center gap-1">
                                                            <Mail size={12} /> {selectedTicket.customerEmail}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Subject</label>
                                                <div className="text-gray-900 font-medium">{selectedTicket.subject}</div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Message</label>
                                                <div className="text-gray-600 bg-gray-50 p-4 rounded-lg text-sm border border-gray-100 whitespace-pre-wrap">
                                                    {selectedTicket.message}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="w-full md:w-64 space-y-6">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Status</label>
                                                <div className="flex flex-col gap-2">
                                                    {['Open', 'In Progress', 'Resolved', 'Closed'].map((s) => (
                                                        <button
                                                            key={s}
                                                            onClick={() => handleUpdateTicketStatus(selectedTicket.id, s)}
                                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${selectedTicket.status === s
                                                                ? 'bg-blue-50 border-blue-200 text-blue-700'
                                                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {s === 'Resolved' ? <CheckCircle size={14} /> : s === 'Open' ? <AlertCircle size={14} /> : null}
                                                            {s}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="pt-4 border-t border-gray-100">
                                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Assigned Agent</label>
                                                <div className="text-gray-600 text-sm">{selectedTicket.assignedAgent || 'Unassigned'}</div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Created At</label>
                                                <div className="text-gray-600 text-sm">{formatDate(selectedTicket.createdAt)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end">
                                    <button onClick={() => setSelectedTicket(null)} className="px-4 py-2 bg-blue-900 text-white rounded-lg font-medium">Done</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <FeedbackDialog
                    isOpen={feedback.isOpen}
                    onClose={() => setFeedback(prev => ({ ...prev, isOpen: false }))}
                    type={feedback.type}
                    title={feedback.title}
                    message={feedback.message}
                    primaryActionLabel={feedback.primaryActionLabel}
                    onPrimaryAction={feedback.onPrimaryAction}
                    secondaryActionLabel={feedback.secondaryActionLabel}
                />
        </div>
    );
}
